import { useRouter } from 'next/router';
import { sideBarNavs } from '../../constants/navigationMeta';
import { RemoveLastUrlPartOf } from '../../utility/common';
import { useDispatch, useSelector } from 'react-redux';
import { Breadcrumbs, Button, CircularProgress } from '@material-ui/core';
import { ArrowRight } from '@material-ui/icons';
import { CSVLink } from 'react-csv';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useQalert, useSchema } from '../../../src/utility/services/customHooks';
import AddPageToOptimize from '../content-recommendations/AddPageToOptimize';
import AddGSCPageToOptimizeModal from '../content-recommendations/AddGSCPageToOptimizeModal';
import { COMPONENT_NAMES, SCREEN_NAMES, trackAnalyticsEvent } from '../../constants/analyticsMeta';
import { dispatchMasterDataQuery } from '../../utility/services/api/common';
import { getHostNameList } from '../../utility/validation/urls';
import { isEmpty } from 'lodash';
import { actionTypes } from '../../constants/actionTypes';
import ApiService from '../../api/restApi';
import IfComponent from '../shared/IfComponent';

/**
 * @param {object} props
 * @param {boolean} [props.withSegmentFilter]
 * @param {any} [props.exportData]
 * @param {any} [props.onExportAll]
 * @param {import('react').ReactNode} [props.endContent] Shown at the right end of the title row,
 *   e.g. the date range selector.
 */
export default function QsectionHeader({
  withSegmentFilter,
  exportData = undefined,
  onExportAll = undefined,
  endContent = undefined,
}) {
  const router = useRouter();
  const headerInfo = useSelector((state) => state.root.headerInfo);
  const customerInfo = useSelector((state) => state.root.customerInfo || []);
  const schemaName = useSchema();
  const customerData = customerInfo.find((o) => o.cust_code == schemaName);
  const isContentAi = customerData?.sidebar_link_config?.['is-content-enabled-only'];
  const isDemoUser = customerData?.additional_user_info?.isDemoUser;
  const [showCreateNewPagePopUp, setShowCreateNewPagePopUp] = useState(false);
  const { pathname } = router;
  const containsDynamicRoute = pathname.includes('[name]') || pathname.includes('[id]');
  const [showCreateNewPagePopUpByUrl, setShowCreateNewPagePopUpByUrl] = useState(false);
  const [domainsList, setDomainsList] = useState([]);
  const dispatch = useDispatch();
  const [, toggleSnackBar] = useQalert();
  const [isApiInProgress, setIsApiInProgress] = useState(false);

  const masterLanguageAndCountry = useSelector((state) => state.root.masterLanguageAndCountry);

  let allNavs = [];
  sideBarNavs.forEach((i) => {
    // Flat top-level nav entries (no subNavs) get treated as their own entry
    // so route matching below still finds them. Without this guard, spreading
    // undefined throws "i.subNavs is not iterable".
    if (Array.isArray(i.subNavs) && i.subNavs.length > 0) {
      allNavs = [...allNavs, ...i.subNavs];
    } else if (i.to) {
      allNavs = [...allNavs, i];
    }
  });
  let matchUrl = containsDynamicRoute ? RemoveLastUrlPartOf(router.pathname, 2) : router.asPath;

  let currentNav = allNavs.find((i) => i.to === matchUrl);
  if (!currentNav) {
    matchUrl = containsDynamicRoute ? RemoveLastUrlPartOf(router.pathname, 1) : router.asPath;
    currentNav = allNavs.find((i) => i.to === matchUrl);
  }
  if (!currentNav) {
    matchUrl = pathname;
    currentNav = allNavs.find((i) => i.to === matchUrl);
  }
  let title = currentNav && currentNav.title ? currentNav.title : '';
  if (currentNav && currentNav.parentTitle == 'Technical Recommendations') {
    title = `${title} Recommendations`;
  }

  const fetchDomains = async () => {
    try {
      const [masterDomainResponse, domainSetupResponse] = await Promise.all([
        dispatchMasterDataQuery(dispatch, ['domain', 'market_share_segment']),
        ApiService.get(`${process.env.NEXT_PUBLIC_QUATTR_API_URL}/api/settings/setup?type=domain`),
      ]);

      const allDomains = [...(getHostNameList(domainSetupResponse?.data?.domainSetup?.SUPPORTED_DOMAINS) || [])];

      if (masterDomainResponse?.domain?.length) {
        masterDomainResponse?.domain?.forEach((domain) => {
          // domains.push(new URL(domain?.top_data)?.origin); //// origin will use to check url starts with same origin i.e http?[s]://abc.com/
          allDomains.push(new URL(domain?.top_data)?.hostname); //// it will check url have same hostname or not example abc.com or www.abc.com
        });
      }
      setDomainsList(allDomains);
    } catch (error) {
      console.error('AddPageToOptimize:: fetchDomains::error::', error);
    }
  };

  const getLanguageAndCountryData = async () => {
    try {
      const response = await ApiService.get(
        `${process.env.NEXT_PUBLIC_QUATTR_API_URL}/api/master-data/country-language`
      );
      dispatch({
        type: actionTypes.MASTER_COUNTRY_AND_LANGUAGE,
        payload: response.data,
      });
    } catch (error) {
      toggleSnackBar({
        type: 'error',
        text: 'Oops! Something Went Wrong. Please try again.',
        open: true,
      });
      setShowCreateNewPagePopUp(false);
    }
  };

  const fetchRequiredData = async () => {
    setIsApiInProgress(true);
    try {
      const promises = [fetchDomains()];
      if (isEmpty(masterLanguageAndCountry)) {
        promises.push(getLanguageAndCountryData());
      }
      await Promise.all(promises);
    } catch (error) {
      console.error('AddPageToOptimize::fetchRequiredData::error::', error);
    } finally {
      setIsApiInProgress(false);
    }
  };

  useEffect(() => {
    fetchRequiredData();
  }, []);

  const isCrPaidPageWithGoogleAddSupport = useMemo(() => {
    return currentNav?.to?.toLowerCase()?.includes('type=paid') && customerData?.isGoogleAdsSupported;
  }, [currentNav?.to, customerData?.isGoogleAdsSupported]);

  return currentNav ? (
    <div
      className={`q-section-header ${!withSegmentFilter ? 'container-wrapper' : 'pd-0'} `}
      data-testid="QsectionHeaderWrapper"
    >
      <div id="layout__div__q_section_header_0" className="dflex">
        <div id="layout__div__q_section_header_1" className="d-inline-block section-icon">
          {currentNav.iconLarge || currentNav.icon}
        </div>
        <span id="layout__div__q_section_header_2">
          <div id="layout__div__q_section_header_3">
            {
              <div id="layout__div__q_section_header_4" className="breadcrumb-wrapper">
                <Breadcrumbs separator={<ArrowRight />} aria-label="breadcrumb">
                  <span>{currentNav.parentTitle}</span>
                </Breadcrumbs>
              </div>
            }
          </div>
          <div id="layout__div__q_section_header_5">
            {' '}
            <h4 id="layout__h4__q_section_header_6" className="q-section-header-title">
              {' '}
              {!headerInfo.title
                ? title === 'Linking Modules'
                  ? 'Internal Linking Modules'
                  : title
                : headerInfo.title}
            </h4>{' '}
          </div>
        </span>
      </div>
      {onExportAll ? (
        <div id="layout__div__q_section_header_7" className="header-actions">
          <Button
            id="layout__button__q_section_header_8"
            disabled={exportData?.isExporting}
            variant="contained"
            color="primary"
            onClick={() => {
              trackAnalyticsEvent({
                eventFor: 'button',
                buttonText: '',
                payload: {
                  label: 'Export',
                  component_name: COMPONENT_NAMES.usability_recommendations,
                  screen_name: SCREEN_NAMES.usability_recommendations,
                },
              });
              onExportAll();
            }}
          >
            {exportData?.isExporting ? (
              <span id="layout__buttontext__q_section_header_9">
                <CircularProgress size={16} style={{ color: '#fff', marginRight: 8 }} />
                {exportData.exportProgress || 'Exporting...'}
              </span>
            ) : (
              <span id="layout__buttontext__q_section_header_9">Export</span>
            )}
          </Button>
        </div>
      ) : exportData ? (
        <div id="layout__div__q_section_header_7" className="header-actions">
          <CSVLink
            data={exportData.data}
            headers={exportData.header}
            filename={`Quattr ${currentNav.parentTitle}.csv`}
            onClick={() => {
              trackAnalyticsEvent({
                eventFor: 'button',
                buttonText: '',
                payload: {
                  label: 'Export',
                  component_name: COMPONENT_NAMES.usability_recommendations,
                  screen_name: SCREEN_NAMES.usability_recommendations,
                },
              });
              return exportData.data.length ? true : false;
            }}
          >
            <Button
              id="layout__button__q_section_header_8"
              disabled={exportData.data.length ? false : true}
              variant="contained"
              color="primary"
            >
              <span id="layout__buttontext__q_section_header_9">Export</span>
            </Button>
          </CSVLink>
        </div>
      ) : null}
      {currentNav.showCreateNewUrlButton || isCrPaidPageWithGoogleAddSupport ? (
        <span>
          {customerData && isContentAi && isContentAi != undefined ? (
            <Button
              className={`create-intent-cta`}
              variant={'outlined'}
              color="primary"
              onClick={() => {
                trackAnalyticsEvent({
                  eventFor: 'button',
                  buttonText: 'form open',
                  payload: {
                    label: 'Add Page By URL',
                    component_name: COMPONENT_NAMES.content_recommendations,
                    screen_name: SCREEN_NAMES.content_recommendations,
                  },
                });
                setShowCreateNewPagePopUpByUrl(true);
              }}
              disabled={!customerData}
            >
              {!customerData ? <CircularProgress className="position-abs" size={20} /> : null}
              Add Page By URL
            </Button>
          ) : null}
          <IfComponent isValid={(customerData && isContentAi != undefined) || isDemoUser}>
            <Button
              className="q-section-header-btn"
              data-testid="add-new-page-Button"
              variant="contained"
              color="primary"
              onClick={() => {
                trackAnalyticsEvent({
                  eventFor: 'button',
                  buttonText: 'form open',
                  payload: {
                    label: customerData && !isContentAi ? 'Add Page To Optimize' : 'Discover Pages to Optimize',
                    component_name: COMPONENT_NAMES.content_recommendations,
                    screen_name: SCREEN_NAMES.content_recommendations,
                  },
                });
                setShowCreateNewPagePopUp(true);
              }}
              disabled={!customerData || isApiInProgress}
            >
              {!customerData || isApiInProgress ? <CircularProgress className="position-abs" size={20} /> : null}
              {(customerData && !isContentAi) || isDemoUser ? 'Add Page To Optimize' : 'Discover Pages to Optimize'}
            </Button>
          </IfComponent>
          {showCreateNewPagePopUp && isContentAi ? (
            <AddGSCPageToOptimizeModal setShowCreateNewPagePopUp={setShowCreateNewPagePopUp} />
          ) : showCreateNewPagePopUp && !isContentAi ? (
            <AddPageToOptimize
              setShowCreateNewPagePopUp={setShowCreateNewPagePopUp}
              isFromDiscoverPagesModal={false}
              discoverPagesModalSubmithandler={false}
              domainsListFetched={domainsList}
            />
          ) : null}
          {showCreateNewPagePopUpByUrl ? (
            <AddPageToOptimize
              setShowCreateNewPagePopUp={setShowCreateNewPagePopUpByUrl}
              isFromDiscoverPagesModal={false}
              discoverPagesModalSubmithandler={false}
              domainsListFetched={domainsList}
            />
          ) : null}
        </span>
      ) : null}

      {currentNav?.customComponent ? currentNav?.customComponent : null}
      {endContent || null}
    </div>
  ) : null;
}
